import { useEffect, useMemo, useState } from "react"
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  BarChart3,
  Disc3,
  Flag,
  LayoutGrid,
  Menu,
  RadioTower,
  Search,
  Settings2,
  Database,
  Users,
  Wrench,
  ScrollText,
  X,
  Wifi,
  Server,
  Loader2,
  LogIn,
  LogOut,
  ShieldCheck,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import { developerLogin, developerLogout, getAdminAnalytics, getDeveloperSession } from "@/lib/api"

const developerNavItems = [
  { label: "Dashboard", to: "/developer/console", icon: LayoutGrid, end: true },
  { label: "Song Catalog", to: "/developer/console/song-catalog", icon: Disc3 },
  { label: "Search", to: "/developer/console/search", icon: Search },
  { label: "Rooms", to: "/developer/console/rooms", icon: RadioTower },
  { label: "Users", to: "/developer/console/users", icon: Users },
  { label: "Analytics", to: "/developer/console/analytics", icon: BarChart3 },
  { label: "System", to: "/developer/console/system", icon: Settings2 },
  { label: "Logs", to: "/developer/console/logs", icon: ScrollText },
  { label: "Developer Tools", to: "/developer/console/developer-tools", icon: Wrench },
  { label: "Feature Flags", to: "/developer/console/feature-flags", icon: Flag },
]

export function DeveloperGate() {
  const location = useLocation()
  const [status, setStatus] = useState("checking")

  useEffect(() => {
    let alive = true

    getDeveloperSession()
      .then(() => {
        if (!alive) return
        setStatus("authenticated")
      })
      .catch(() => {
        if (!alive) return
        setStatus("unauthenticated")
      })

    return () => {
      alive = false
    }
  }, [])

  if (status === "checking") {
    return (
      <div className="ambient-bg min-h-screen relative overflow-hidden">
        <div className="grain" />
        <div className="relative z-10 flex min-h-screen items-center justify-center p-6">
          <Card className="glass border-white/5 w-full max-w-md">
            <CardContent className="flex flex-col items-center gap-4 p-10 text-center">
              <div className="w-14 h-14 rounded-2xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-purple-200">
                <Loader2 className="animate-spin" size={24} />
              </div>
              <div>
                <div className="text-white font-semibold text-lg font-[Outfit]">Checking developer session</div>
                <div className="text-zinc-400 mt-1 text-sm">Validating access with the backend.</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (status !== "authenticated") {
    return <Navigate to="/developer/login" replace state={{ from: location.pathname }} />
  }

  return <Outlet />
}

export function DeveloperConsoleLayout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const activeItem =
    developerNavItems.find((item) =>
      item.end
        ? location.pathname === item.to
        : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
    ) || developerNavItems[0]

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      await developerLogout()
    } catch {
      // Ignore logout errors and continue to the login screen.
    } finally {
      setLoggingOut(false)
      navigate("/developer/login", { replace: true })
    }
  }

  const navLinkClass = ({ isActive }) =>
    [
      "flex items-center gap-3 rounded-2xl px-4 py-3 transition-all duration-200",
      isActive
        ? "bg-purple-500/20 text-white border border-purple-500/30 shadow-[0_0_20px_rgba(139,92,246,0.16)]"
        : "text-zinc-300 hover:bg-white/5 hover:text-white border border-transparent",
    ].join(" ")

  const navList = (prefix) =>
    developerNavItems.map(({ label, to, icon: Icon, end }) => (
      <NavLink key={`${prefix}-${to}`} to={to} end={end} className={navLinkClass} data-testid={`${prefix}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        <Icon size={18} className="shrink-0" />
        <span className="font-medium">{label}</span>
      </NavLink>
    ))

  return (
    <div className="ambient-bg min-h-screen relative overflow-hidden">
      <div className="grain" />
      <div className="relative z-10 flex min-h-screen">
        <aside className="hidden lg:flex w-[320px] flex-col border-r border-white/5 bg-black/10 backdrop-blur-2xl">
          <div className="p-6">
            <div className="inline-flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center shadow-[0_0_24px_rgba(139,92,246,0.55)]">
                <Wrench size={20} className="text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 leading-none">
                  Protected
                </div>
                <div className="text-white font-bold text-xl font-[Outfit] leading-tight">
                  Developer Console
                </div>
              </div>
            </div>
          </div>

          <Separator className="bg-white/5" />

          <ScrollArea className="flex-1">
            <nav className="p-4 space-y-2">{navList("developer-sidebar")}</nav>
          </ScrollArea>

          <div className="p-6">
            <Card className="glass border-white/5">
              <CardHeader className="p-5 pb-3">
                <CardTitle className="text-sm uppercase tracking-[0.25em] text-zinc-500">
                  Current View
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5 pt-0">
                <div className="text-xl font-bold text-white font-[Outfit]">
                  {activeItem.label}
                </div>
              </CardContent>
            </Card>
          </div>
        </aside>

        <div className="flex min-h-screen flex-1 flex-col">
          <header className="sticky top-0 z-30 border-b border-white/5 glass">
            <div className="flex items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3 min-w-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="lg:hidden rounded-full bg-zinc-900/60 border border-white/5 text-white hover:bg-zinc-800"
                  onClick={() => setMobileOpen(true)}
                  data-testid="developer-open-menu"
                >
                  <Menu size={18} />
                </Button>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 leading-none">
                    OpenKTV
                  </div>
                  <h1 className="text-xl sm:text-2xl font-bold text-white font-[Outfit] leading-tight truncate">
                    {activeItem.label}
                  </h1>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/25 text-purple-200 text-xs uppercase tracking-[0.2em]">
                Developer access
              </div>
              <Button
                type="button"
                variant="outline"
                className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10"
                onClick={handleLogout}
                disabled={loggingOut}
              >
                {loggingOut ? <Loader2 className="animate-spin" size={16} /> : <LogOut size={16} />}
                Logout
              </Button>
            </div>
          </header>

          <main className="flex-1 p-4 sm:p-6 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/70 backdrop-blur-md lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
              className="fixed left-0 top-0 bottom-0 z-50 w-[85vw] max-w-[320px] border-r border-white/5 bg-zinc-950/95 backdrop-blur-2xl lg:hidden"
            >
              <div className="flex items-center justify-between px-5 py-5 border-b border-white/5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 leading-none">
                    Developer Console
                  </div>
                  <div className="text-white font-bold text-lg font-[Outfit]">
                    Navigation
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="rounded-full bg-zinc-900/60 border border-white/5 text-white hover:bg-zinc-800"
                  onClick={() => setMobileOpen(false)}
                  data-testid="developer-close-menu"
                >
                  <X size={18} />
                </Button>
              </div>
              <ScrollArea className="h-[calc(100%-73px)]">
                <nav className="p-4 space-y-2">
                  {navList("developer-mobile")}
                </nav>
              </ScrollArea>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export function DeveloperLoginPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    let alive = true

    getDeveloperSession()
      .then(() => {
        if (!alive) return
        navigate("/developer/console", { replace: true })
      })
      .catch(() => {
        if (!alive) return
        setChecking(false)
      })

    return () => {
      alive = false
    }
  }, [navigate])

  const handleSubmit = async (event) => {
    event.preventDefault()
    setLoading(true)
    setError("")
    try {
      await developerLogin({ username: username.trim(), password })
      navigate(location.state?.from || "/developer/console", { replace: true })
    } catch (err) {
      setError(err?.response?.data?.detail || "Developer login failed")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="ambient-bg min-h-screen relative overflow-hidden">
      <div className="grain" />
      <div className="relative z-10 flex min-h-screen items-center justify-center p-6">
        <Card className="glass border-white/5 w-full max-w-md overflow-hidden">
          <CardContent className="p-8 sm:p-10">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center shadow-[0_0_30px_rgba(139,92,246,0.6)] mb-6">
              <ShieldCheck size={26} className="text-white" strokeWidth={2.4} />
            </div>

            <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300 mb-3">
              Developer Console
            </div>
            <h1 className="text-4xl font-bold text-white font-[Outfit] mb-2">Sign In</h1>
            <p className="text-zinc-400 mb-8">
              Access is enforced by the backend. The frontend gate is only a convenience layer.
            </p>

            {checking ? (
              <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-5 text-sm text-zinc-300 flex items-center gap-3">
                <Loader2 className="animate-spin" size={16} />
                Checking existing developer session...
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    className="input-base"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    autoFocus
                    maxLength={80}
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    className="input-base"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    maxLength={200}
                  />
                </div>

                {error ? (
                  <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                    {error}
                  </div>
                ) : null}

                <Button type="submit" className="w-full" disabled={loading || !username.trim() || !password}>
                  {loading ? <Loader2 className="animate-spin" size={16} /> : <LogIn size={16} />}
                  Sign In
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export function DeveloperPlaceholderPage({ title }) {
  return (
    <Card className="glass border-white/5 min-h-[calc(100vh-10rem)]">
      <CardContent className="flex min-h-[calc(100vh-10rem)] items-center justify-center p-6 sm:p-10">
        <div className="text-center max-w-xl">
          <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300 mb-4">
            Developer Console
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white font-[Outfit] leading-tight">
            {title}
          </h2>
        </div>
      </CardContent>
    </Card>
  )
}

const dashboardCards = [
  { key: "usersOnline", label: "Users Online", icon: Users, tone: "purple", description: "Active users currently connected" },
  { key: "activeRooms", label: "Active Rooms", icon: RadioTower, tone: "purple", description: "Live karaoke sessions" },
  { key: "totalSongs", label: "Songs in Catalog", icon: Disc3, tone: "purple", description: "Available songs for queuing" },
  { key: "totalSearches", label: "Total Searches", icon: Search, tone: "purple", description: "Total search queries performed" },
  { key: "youtubeSearches", label: "YouTube Searches", icon: Search, tone: "purple", description: "Searches hitting YouTube API" },
  { key: "queueCount", label: "Queue Count", icon: LayoutGrid, tone: "purple", description: "Total queued songs across rooms" },
  { key: "cacheHitRate", label: "Cache Hit Rate", icon: BarChart3, tone: "emerald", percent: true, description: "YouTube metadata cache efficiency" },
  { key: "youtubeApiCallsSaved", label: "YouTube API Calls Saved", icon: ScrollText, tone: "purple", description: "Calls avoided via caching" },
  { key: "backendStatus", label: "Backend Status", icon: Server, tone: "slate", status: true, description: "API and routing response status" },
  { key: "mongoStatus", label: "MongoDB Status", icon: Database, tone: "slate", status: true, description: "Mongo database connectivity" },
  { key: "socketConnections", label: "Socket Connections", icon: Wifi, tone: "purple", description: "Active WebSocket connections" },
]

const metricTone = {
  purple: "border-purple-500/30 bg-purple-500/10 text-purple-200",
  emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
  slate: "border-white/10 bg-white/5 text-zinc-200",
}

function formatMetric(card, value) {
  if (value === null || value === undefined || value === "") return "Coming Soon"
  if (card.status) return String(value)
  if (card.percent) return `${Math.round(Number(value) * 1000) / 10}%`
  if (typeof value === "number") return value.toLocaleString()
  return String(value)
}

export function DeveloperDashboardPage() {
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    getAdminAnalytics(5)
      .then((data) => {
        if (!alive) return
        setAnalytics(data)
        setError(false)
      })
      .catch(() => {
        if (!alive) return
        setError(true)
        setAnalytics(null)
      })
      .finally(() => {
        if (!alive) return
        setLoading(false)
      })

    return () => {
      alive = false
    }
  }, [])

  const cards = useMemo(
    () =>
      dashboardCards.map((card) => ({
        ...card,
        value: analytics ? analytics[card.key] : null,
      })),
    [analytics]
  )

  return (
    <div className="space-y-6">
      <Card className="glass border-white/5 overflow-hidden">
        <CardContent className="p-6 sm:p-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300 mb-2">
              Developer Console
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-[Outfit] leading-tight">
              Dashboard
            </h2>
            <p className="text-zinc-400 mt-2 max-w-2xl">
              Live platform signals from the existing OpenKTV backend. Metrics are sourced from the current MongoDB collections and Socket.IO session state.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="border-white/10 bg-white/5 text-zinc-200">
              Live data
            </Badge>
            <Badge variant="outline" className="border-white/10 bg-white/5 text-zinc-200">
              Existing analytics endpoint
            </Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => {
          const value = loading ? "Loading..." : formatMetric(card, error ? null : card.value)
          return (
            <Card key={card.key} className="glass border-white/5 hover:border-purple-500/30 transition-colors">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
                      {card.label}
                    </div>
                    <div className="text-3xl sm:text-4xl font-bold text-white font-[Outfit] leading-none">
                      {value}
                    </div>
                  </div>
                  <div className={`w-11 h-11 rounded-2xl border flex items-center justify-center ${metricTone[card.tone]}`}>
                    <card.icon size={18} strokeWidth={2.3} />
                  </div>
                </div>
                {card.status ? (
                  <div className="mt-4 text-sm text-zinc-400">
                    {card.key === "mongoStatus" && analytics?.mongoStatus === "Degraded"
                      ? "Mongo ping failed"
                      : card.key === "backendStatus"
                      ? "API and routing are responding"
                      : card.description}
                  </div>
                ) : (
                  <div className="mt-4 text-sm text-zinc-400">
                    {error ? "Coming Soon" : card.description}
                  </div>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      <Card className="glass border-white/5">
        <CardHeader className="p-6 pb-3">
          <CardTitle className="text-sm uppercase tracking-[0.25em] text-zinc-500">
            Status Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-4">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Backend</div>
            <div className="mt-2 text-lg font-semibold text-white">{loading ? "Loading..." : analytics?.backendStatus || "Coming Soon"}</div>
          </div>
          <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-4">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">MongoDB</div>
            <div className="mt-2 text-lg font-semibold text-white">{loading ? "Loading..." : analytics?.mongoStatus || "Coming Soon"}</div>
          </div>
          <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-4">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Sockets</div>
            <div className="mt-2 text-lg font-semibold text-white">{loading ? "Loading..." : analytics?.socketConnections ?? "Coming Soon"}</div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
